import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import {
  getLeadsThisWeek,
  getFormSubmissionsThisWeek,
  getPageViewsThisWeek,
  getDashboardFormStats,
  getPageStatsForWebsite,
} from '../services/analyticsService';
import {
  getTodosWithSource,
  createTodo,
  toggleTodoCompleted,
  categorizeTodo,
} from '../services/todoService';
import { supabase } from '../lib/supabase';
import { ToDoCreateForm } from '../components/todos';
import type { TodoWithSource, CreateTodoInput } from '../types/todo';
import {
  CheckCircle2,
  Circle,
  Users,
  UserPlus,
  CalendarDays,
  TrendingUp,
  FileText,
  Eye,
  Send,
  Globe,
  BarChart3,
  Plus,
  Edit3,
  User,
  ArrowRight,
  CheckSquare,
  Trophy,
  Rocket,
  PartyPopper,
} from 'lucide-react';
import { GamificationWidget } from '../components/gamification';
import { logger } from '../lib/logger';

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  NEW: { label: 'Neu', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  CONTACTED: { label: 'Kontaktiert', color: 'text-amber-600', bgColor: 'bg-amber-100' },
  IN_DISCUSSION: { label: 'Im Gespräch', color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
  WON: { label: 'Gewonnen', color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  LOST: { label: 'Verloren', color: 'text-gray-500', bgColor: 'bg-gray-100' }
};

interface LeadStats {
  total: number;
  newThisWeek: number;
  newToday: number;
  byStatus: Record<string, number>;
}

interface PageStats {
  published: boolean;
  views: number;
  submissions: number;
  conversion: number;
}

interface FormStats {
  name: string;
  views: number;
  submissions: number;
  conversion: number;
}

interface SetupStep {
  id: number;
  text: string;
  done: boolean;
}

function getMotivationMessage(completed: number, total: number): { text: string; icon: 'rocket' | 'party' } {
  if (total === 0) return { text: 'Erstelle dein erstes Todo!', icon: 'rocket' };
  if (completed === 0) return { text: 'Starte jetzt!', icon: 'rocket' };

  const percentage = (completed / total) * 100;

  if (percentage === 100) return { text: 'Perfekt! Alles erledigt!', icon: 'party' };
  if (percentage >= 80) return { text: 'Fast geschafft!', icon: 'rocket' };
  if (percentage >= 50) return { text: 'Die Hälfte ist geschafft!', icon: 'rocket' };
  if (percentage >= 25) return { text: 'Weiter so!', icon: 'rocket' };
  return { text: 'Gib Gas!', icon: 'rocket' };
}

export function Dashboard() {
  const { user, profile } = useAuth();
  const { primaryColor } = useSettings();
  const [todos, setTodos] = useState<TodoWithSource[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [leadStats, setLeadStats] = useState<LeadStats>({
    total: 0,
    newThisWeek: 0,
    newToday: 0,
    byStatus: { NEW: 0, CONTACTED: 0, IN_DISCUSSION: 0, WON: 0, LOST: 0 }
  });
  const [businessPageStats, setBusinessPageStats] = useState<PageStats>({
    published: false, views: 0, submissions: 0, conversion: 0
  });
  const [productsPageStats, setProductsPageStats] = useState<PageStats>({
    published: false, views: 0, submissions: 0, conversion: 0
  });
  const [formStats, setFormStats] = useState<FormStats[]>([]);
  const [setupSteps, setSetupSteps] = useState<SetupStep[]>([]);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user?.id]);

  const loadDashboardData = async () => {
    if (!user?.id) return;
    setIsLoading(true);

    try {
      const [
        leadsWeek,
        ,
        ,
        formStatsData,
        businessStats,
        productsStats,
        todosData,
      ] = await Promise.all([
        getLeadsThisWeek(user.id),
        getFormSubmissionsThisWeek(user.id),
        getPageViewsThisWeek(user.id),
        getDashboardFormStats(user.id),
        getPageStatsForWebsite(user.id, 'BUSINESS'),
        getPageStatsForWebsite(user.id, 'PRODUCTS'),
        getTodosWithSource(user.id),
      ]);

      setTodos(todosData);

      const { count: totalLeads } = await supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { count: leadsToday } = await supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('created_at', today.toISOString());

      const { data: leadsByStatus } = await supabase
        .from('leads')
        .select('status')
        .eq('user_id', user.id);

      const statusCounts: Record<string, number> = {
        NEW: 0, CONTACTED: 0, IN_DISCUSSION: 0, WON: 0, LOST: 0
      };
      leadsByStatus?.forEach((lead) => {
        if (lead.status && Object.prototype.hasOwnProperty.call(statusCounts, lead.status)) {
          statusCounts[lead.status]++;
        }
      });

      setLeadStats({
        total: totalLeads || 0,
        newThisWeek: leadsWeek,
        newToday: leadsToday || 0,
        byStatus: statusCounts,
      });

      const { data: businessPage } = await supabase
        .from('site_pages')
        .select('is_published')
        .eq('user_id', user.id)
        .eq('type', 'BUSINESS')
        .maybeSingle();

      const { data: productsPage } = await supabase
        .from('site_pages')
        .select('is_published')
        .eq('user_id', user.id)
        .eq('type', 'PRODUCTS')
        .maybeSingle();

      setBusinessPageStats({
        published: businessPage?.is_published || false,
        ...businessStats,
      });

      setProductsPageStats({
        published: productsPage?.is_published || false,
        ...productsStats,
      });

      setFormStats(formStatsData);

      const { count: formsCount } = await supabase
        .from('forms')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);

      const steps: SetupStep[] = [
        { id: 1, text: 'Profil vervollständigen', done: !!profile?.full_name },
        { id: 2, text: 'Business-Seite veröffentlichen', done: businessPage?.is_published || false },
        { id: 3, text: 'Produktseite veröffentlichen', done: productsPage?.is_published || false },
        { id: 4, text: 'Formular erstellen', done: (formsCount || 0) > 0 },
        { id: 5, text: 'WhatsApp Nummer eintragen', done: !!profile?.phone },
        { id: 6, text: 'Social Media verknüpfen', done: !!(profile?.instagram_url || profile?.facebook_url || profile?.tiktok_url) },
      ];
      setSetupSteps(steps);

    } catch (error) {
      logger.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTodo = async (id: string, completed: boolean) => {
    if (!user?.id) return;
    try {
      await toggleTodoCompleted(id, user.id, completed);
      setTodos((prev) =>
        prev.map((t) => (t.id === id ? { ...t, completed } : t))
      );
    } catch (error) {
      logger.error('Error toggling todo:', error);
    }
  };

  const handleCreateTodo = async (input: CreateTodoInput) => {
    if (!user?.id) return;
    await createTodo(user.id, input);
    // Reload todos to get fresh data with source info
    const todosData = await getTodosWithSource(user.id);
    setTodos(todosData);
    setShowCreateForm(false);
  };

  // Filter todos for today/overdue and upcoming
  const todayAndOverdueTodos = todos.filter((t) => {
    if (t.completed) return false;
    const group = categorizeTodo(t.due_date, t.completed);
    return group === 'OVERDUE' || group === 'TODAY';
  });

  const upcomingTodos = todos.filter((t) => {
    if (t.completed) return false;
    const group = categorizeTodo(t.due_date, t.completed);
    return group === 'TOMORROW' || group === 'THIS_WEEK' || group === 'LATER';
  });

  // For progress: count today's todos (including completed)
  const todaysTodos = todos.filter((t) => {
    const group = categorizeTodo(t.due_date, false); // Check raw due date
    return group === 'TODAY';
  });
  const todaysCompleted = todaysTodos.filter((t) => t.completed).length;
  const todaysTotal = todaysTodos.length;
  const todaysPercentage = todaysTotal > 0 ? Math.round((todaysCompleted / todaysTotal) * 100) : 0;
  const motivation = getMotivationMessage(todaysCompleted, todaysTotal);

  // Determine which todos to show
  const hasOpenTodayOrOverdue = todayAndOverdueTodos.length > 0;
  const displayTodos = hasOpenTodayOrOverdue ? todayAndOverdueTodos : upcomingTodos;
  const showAllButton = displayTodos.length > 5;
  const visibleTodos = displayTodos.slice(0, 5);

  const firstName = profile?.full_name?.split(' ')[0] || 'Benutzer';
  const completedSteps = setupSteps.filter(s => s.done).length;
  const setupPercentage = setupSteps.length > 0 ? Math.round((completedSteps / setupSteps.length) * 100) : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="bt-h1">
          Willkommen, <em>{firstName}</em>
        </h1>
        <p style={{ color: 'var(--bt-mute)', fontSize: 14, fontWeight: 500 }}>
          Hier ist dein Überblick für heute.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <div data-tour="todo-section" className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckSquare className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-base sm:text-lg font-semibold text-[#1A1A1A] dark:text-white">Was heute wichtig ist</h2>
              </div>
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg transition-colors text-sm font-medium"
                style={{ backgroundColor: primaryColor }}
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Neues Todo</span>
              </button>
            </div>

            {/* Progress Bar für heute */}
            {todaysTotal > 0 && (
              <div className="mb-5 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-100 dark:border-amber-800">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {motivation.icon === 'party' ? (
                      <PartyPopper className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    ) : (
                      <Rocket className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    )}
                    <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">{motivation.text}</span>
                  </div>
                  <span className="text-sm font-bold text-amber-700 dark:text-amber-400">{todaysCompleted}/{todaysTotal}</span>
                </div>
                <div className="h-2.5 bg-white dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full transition-all duration-500"
                    style={{ width: `${todaysPercentage}%` }}
                  />
                </div>
              </div>
            )}

            {/* Create Form */}
            {showCreateForm && (
              <div className="mb-5">
                <ToDoCreateForm
                  onSubmit={handleCreateTodo}
                  onCancel={() => setShowCreateForm(false)}
                />
              </div>
            )}

            {/* Todos List */}
            <div className="space-y-3">
              {!hasOpenTodayOrOverdue && upcomingTodos.length === 0 && !showCreateForm && (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <PartyPopper className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <p className="text-emerald-700 dark:text-emerald-400 font-medium mb-1">Super! Für heute hast du alles erledigt.</p>
                  <p className="text-sm text-[#6B6B6B] dark:text-slate-400">Genieße den Rest des Tages!</p>
                </div>
              )}

              {!hasOpenTodayOrOverdue && upcomingTodos.length > 0 && (
                <div className="text-center py-4 mb-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
                  <div className="flex items-center justify-center gap-2 text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Für heute alles erledigt!</span>
                  </div>
                  <p className="text-sm text-emerald-600 dark:text-emerald-500 mt-1">Hier sind deine nächsten Aufgaben:</p>
                </div>
              )}

              {visibleTodos.map((todo) => {
                const todoGroup = categorizeTodo(todo.due_date, todo.completed);
                const isOverdue = todoGroup === 'OVERDUE';
                const groupLabels: Record<string, { text: string; color: string }> = {
                  OVERDUE: { text: 'Überfällig', color: 'text-red-500 dark:text-red-400' },
                  TODAY: { text: 'Heute', color: 'text-amber-600 dark:text-amber-400' },
                  TOMORROW: { text: 'Morgen', color: 'text-cyan-600 dark:text-cyan-400' },
                  THIS_WEEK: { text: 'Diese Woche', color: 'text-blue-600 dark:text-blue-400' },
                  LATER: { text: 'Später', color: 'text-gray-500 dark:text-gray-400' },
                };
                const groupLabel = groupLabels[todoGroup];

                return (
                  <button
                    key={todo.id}
                    onClick={() => handleToggleTodo(todo.id, !todo.completed)}
                    className={`w-full flex items-start gap-3 p-4 rounded-xl transition-all text-left ${
                      todo.completed
                        ? 'bg-gray-50 dark:bg-slate-700/50 text-gray-400 dark:text-slate-500'
                        : isOverdue
                          ? 'bg-red-50/50 dark:bg-red-900/20 hover:bg-red-50 dark:hover:bg-red-900/30 text-[#1A1A1A] dark:text-white'
                          : 'bg-amber-50/50 dark:bg-amber-900/20 hover:bg-amber-50 dark:hover:bg-amber-900/30 text-[#1A1A1A] dark:text-white'
                    }`}
                  >
                    {todo.completed ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Circle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isOverdue ? 'text-red-500' : 'text-amber-500'}`} />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm block ${todo.completed ? 'line-through' : 'font-medium'}`}>
                        {todo.title}
                      </span>
                      <div className="flex items-center gap-2 mt-0.5">
                        {!todo.completed && groupLabel && (
                          <span className={`text-xs font-medium ${groupLabel.color}`}>
                            {groupLabel.text}
                          </span>
                        )}
                        {todo.source && (
                          <span className="text-xs text-[#6B6B6B] dark:text-slate-500 truncate">
                            • {todo.source.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Buttons */}
            {showAllButton && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
                <Link
                  to="/todos"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl transition-colors font-medium text-sm"
                  style={{ color: primaryColor }}
                >
                  Alle {displayTodos.length} Todos anzeigen
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>

          <div data-tour="lead-stats" className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-full">Heute</span>
              </div>
              <p className="text-3xl font-bold text-[#1A1A1A] dark:text-white">{leadStats.newToday}</p>
              <p className="text-sm text-[#6B6B6B] dark:text-slate-400 mt-1">Neue Leads heute</p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-cyan-50 dark:bg-cyan-900/30 rounded-xl flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <span className="text-xs font-medium text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 px-2 py-1 rounded-full">Diese Woche</span>
              </div>
              <p className="text-3xl font-bold text-[#1A1A1A] dark:text-white">{leadStats.newThisWeek}</p>
              <p className="text-sm text-[#6B6B6B] dark:text-slate-400 mt-1">Neue Leads diese Woche</p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-3xl font-bold text-[#1A1A1A] dark:text-white">{leadStats.total}</p>
              <p className="text-sm text-[#6B6B6B] dark:text-slate-400 mt-1">Gesamt Leads</p>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-teal-50 dark:bg-teal-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
              <h2 className="text-base sm:text-lg font-semibold text-[#1A1A1A] dark:text-white">Pipeline Überblick</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
              {Object.entries(leadStats.byStatus).map(([status, count]) => {
                const config = statusConfig[status];
                if (!config) return null;
                return (
                  <div key={status} className={`${config.bgColor} dark:bg-opacity-20 rounded-xl p-4 text-center`}>
                    <p className={`text-2xl font-bold ${config.color}`}>{count}</p>
                    <p className={`text-xs font-medium ${config.color} mt-1`}>{config.label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-50 dark:bg-rose-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                </div>
                <h2 className="text-base sm:text-lg font-semibold text-[#1A1A1A] dark:text-white">Formular-Performance</h2>
              </div>
              <span className="text-xs text-[#6B6B6B] dark:text-slate-400 ml-13 sm:ml-0">Letzte 7 Tage</span>
            </div>
            {formStats.length > 0 ? (
              <>
                {/* Desktop Tabelle */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider">
                        <th className="pb-3 pr-4">Formular</th>
                        <th className="pb-3 pr-4">
                          <div className="flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5" />
                            Aufrufe
                          </div>
                        </th>
                        <th className="pb-3 pr-4">
                          <div className="flex items-center gap-1">
                            <Send className="w-3.5 h-3.5" />
                            Einsendungen
                          </div>
                        </th>
                        <th className="pb-3">Conversion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                      {formStats.map((form, idx) => (
                        <tr key={idx} className="text-sm">
                          <td className="py-3 pr-4 font-medium text-[#1A1A1A] dark:text-white">{form.name}</td>
                          <td className="py-3 pr-4 text-[#6B6B6B] dark:text-slate-400">{form.views}</td>
                          <td className="py-3 pr-4 text-[#6B6B6B] dark:text-slate-400">{form.submissions}</td>
                          <td className="py-3">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                              {form.conversion}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile Karten */}
                <div className="sm:hidden space-y-3">
                  {formStats.map((form, idx) => (
                    <div key={idx} className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                      <p className="font-medium text-[#1A1A1A] dark:text-white mb-3">{form.name}</p>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 text-[#6B6B6B] dark:text-slate-400 mb-1">
                            <Eye className="w-3.5 h-3.5" />
                          </div>
                          <p className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{form.views}</p>
                          <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Aufrufe</p>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 text-[#6B6B6B] dark:text-slate-400 mb-1">
                            <Send className="w-3.5 h-3.5" />
                          </div>
                          <p className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{form.submissions}</p>
                          <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Einsendungen</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{form.conversion}%</p>
                          <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Conversion</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-[#6B6B6B] dark:text-slate-400 text-center py-4">
                Noch keine Formulare erstellt
              </p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-[#1A1A1A] dark:text-white truncate">Business Page</h3>
                  </div>
                </div>
                {businessPageStats.published ? (
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-1 rounded-full flex-shrink-0">
                    Veröffentlicht
                  </span>
                ) : (
                  <span className="text-xs font-medium text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-700 px-2 py-1 rounded-full flex-shrink-0">
                    Entwurf
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-[#1A1A1A] dark:text-white">{businessPageStats.views}</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Aufrufe</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-[#1A1A1A] dark:text-white">{businessPageStats.submissions}</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Anfragen</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-[#1A1A1A] dark:text-white">{businessPageStats.conversion}%</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Conv.</p>
                </div>
              </div>
              <p className="text-xs text-[#6B6B6B] dark:text-slate-400 text-center">Letzte 7 Tage</p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-violet-50 dark:bg-violet-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-[#1A1A1A] dark:text-white truncate">Product Page</h3>
                  </div>
                </div>
                {productsPageStats.published ? (
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-1 rounded-full flex-shrink-0">
                    Veröffentlicht
                  </span>
                ) : (
                  <span className="text-xs font-medium text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-700 px-2 py-1 rounded-full flex-shrink-0">
                    Entwurf
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-[#1A1A1A] dark:text-white">{productsPageStats.views}</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Aufrufe</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-[#1A1A1A] dark:text-white">{productsPageStats.submissions}</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Anfragen</p>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-[#1A1A1A] dark:text-white">{productsPageStats.conversion}%</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Conv.</p>
                </div>
              </div>
              <p className="text-xs text-[#6B6B6B] dark:text-slate-400 text-center">Letzte 7 Tage</p>
            </div>
          </div>
        </div>

        <div className="space-y-6 min-w-0">
          {/* Einrichtung Widget - versteckt bei 100% */}
          {setupPercentage < 100 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-sky-50 dark:bg-sky-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                </div>
                <h2 className="text-base sm:text-lg font-semibold text-[#1A1A1A] dark:text-white">Deine Einrichtung</h2>
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-[#6B6B6B] dark:text-slate-400">Fortschritt</span>
                  <span className="font-semibold text-[#1A1A1A] dark:text-white">{setupPercentage}%</span>
                </div>
                <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-sky-400 to-sky-500 rounded-full transition-all"
                    style={{ width: `${setupPercentage}%` }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                {setupSteps.map((step) => (
                  <div
                    key={step.id}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      step.done ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-slate-700'
                    }`}
                  >
                    {step.done ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${step.done ? 'text-emerald-700 dark:text-emerald-400' : 'text-[#6B6B6B] dark:text-slate-400'}`}>
                      {step.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Schnellzugriff */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-gray-100 dark:bg-slate-700 rounded-xl flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-5 h-5 text-gray-600 dark:text-slate-400" />
              </div>
              <h2 className="text-base sm:text-lg font-semibold text-[#1A1A1A] dark:text-white">Schnellzugriff</h2>
            </div>
            <div className="space-y-2">
              <Link
                to="/leads"
                className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group"
              >
                <Plus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Neuen Lead erstellen</span>
              </Link>
              <Link
                to="/forms"
                className="flex items-center gap-3 p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors group"
              >
                <FileText className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                <span className="text-sm font-medium text-rose-700 dark:text-rose-400">Neues Formular erstellen</span>
              </Link>
              <Link
                to="/website"
                className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors group"
              >
                <Edit3 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Business Page bearbeiten</span>
              </Link>
              <Link
                to="/website"
                className="flex items-center gap-3 p-3 rounded-xl bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors group"
              >
                <Globe className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                <span className="text-sm font-medium text-violet-700 dark:text-violet-400">Product Page bearbeiten</span>
              </Link>
              <Link
                to="/profile"
                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors group"
              >
                <User className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Profil bearbeiten</span>
              </Link>
              <Link
                to="/analytics"
                className="flex items-center gap-3 p-3 rounded-xl bg-cyan-50 dark:bg-cyan-900/20 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors group"
              >
                <BarChart3 className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                <span className="text-sm font-medium text-cyan-700 dark:text-cyan-400">Analytics ansehen</span>
              </Link>
            </div>
          </div>

          {/* Gamification Widget */}
          <GamificationWidget />
        </div>
      </div>
    </div>
  );
}
